import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import API from "../lib/api";
import {
  Loader2,
  ExternalLink,
  Download,
  FileWarning,
  FileText,
} from "lucide-react";

const MAX_TEXT_CHARS = 500000;
const MAX_CSV_ROWS = 50000;
const MAX_XLSX_ROWS = 50000;
const MAX_XLSX_COLS = 300;
const MAX_PARSE_BYTES = 100 * 1024 * 1024;

function getExtension(filename) {
  const ext = String(filename || "").split(".").pop() || "";
  return ext.toLowerCase();
}

const TEXT_EXTENSIONS = ["txt", "md", "markdown", "eml", "log", "text"];
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
const SPREADSHEET_EXTENSIONS = ["xlsx", "xls", "csv"];

function formatSize(size) {
  const bytes = Number(size || 0);
  if (!bytes) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function parseCsvRows(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const input = String(text || "");

  for (let i = 0; i < input.length && rows.length < MAX_CSV_ROWS + 1; i++) {
    const char = input[i];
    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && input[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
    } else {
      field += char;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.slice(0, MAX_CSV_ROWS);
}

function CsvTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No rows found.</p>;
  }
  const maxCols = Math.max(...rows.map((r) => r.length), 0);
  return (
    <div className="overflow-auto border rounded-xl">
      <table className="w-full text-left text-xs border-collapse">
        <thead>
          <tr className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            {Array.from({ length: maxCols }).map((_, i) => (
              <th key={i} className="px-3 py-2 font-bold whitespace-nowrap">
                {rows[0]?.[i] != null ? String(rows[0][i]).slice(0, 60) : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(1).map((row, ri) => (
            <tr key={ri} className="border-t border-border/60 hover:bg-accent/40">
              {Array.from({ length: maxCols }).map((_, ci) => (
                <td key={ci} className="px-3 py-1.5 whitespace-nowrap font-mono text-[11px]">
                  {row[ci] != null ? String(row[ci]).slice(0, 120) : ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SpreadsheetTable({ workbook }) {
  const sheetName = workbook.SheetNames?.[0];
  const rows = useMemo(() => {
    if (!sheetName) return [];
    const ws = workbook.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    const maxRows = Math.min(raw.length, MAX_XLSX_ROWS);
    const maxCols = Math.min(
      Math.max(...raw.slice(0, maxRows).map((r) => r.length), 0),
      MAX_XLSX_COLS
    );
    return raw
      .slice(0, maxRows)
      .map((r) => Array.from({ length: maxCols }, (_, i) => String(r[i] ?? "").slice(0, 120)));
  }, [workbook, sheetName]);

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No data found in the first sheet.</p>;
  }

  return (
    <div className="overflow-auto border rounded-xl">
      <table className="w-full text-left text-xs border-collapse">
        <thead>
          <tr className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            {rows[0].map((cell, i) => (
              <th key={i} className="px-3 py-2 font-bold whitespace-nowrap">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(1).map((row, ri) => (
            <tr key={ri} className="border-t border-border/60 hover:bg-accent/40">
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-1.5 whitespace-nowrap font-mono text-[11px]">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UnsupportedView({ filename, url }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <FileWarning className="h-10 w-10 text-amber-500 mb-3" />
      <p className="text-sm font-semibold text-foreground">Preview not supported for this file type</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-sm">
        {filename}. Open the file in a new tab or download it to view the contents.
      </p>
      <div className="flex gap-2 mt-5">
        {url && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open in new tab
            </Button>
            <a href={url} download>
              <Button size="sm">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download
              </Button>
            </a>
          </>
        )}
      </div>
    </div>
  );
}

export default function FilePreviewDialog({ open, onOpenChange, auditRunId, item, page }) {
  const [status, setStatus] = useState("idle");
  const [presigned, setPresigned] = useState(null);
  const [meta, setMeta] = useState({});
  const [textContent, setTextContent] = useState("");
  const [workbook, setWorkbook] = useState(null);
  const [docxHtml, setDocxHtml] = useState("");

  const ext = getExtension(item?.filename);
  const renderType = useMemo(() => {
    if (IMAGE_EXTENSIONS.includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    if (SPREADSHEET_EXTENSIONS.includes(ext)) return "spreadsheet";
    if (TEXT_EXTENSIONS.includes(ext)) return "text";
    if (ext === "json") return "json";
    if (ext === "docx") return "docx";
    return "unsupported";
  }, [ext]);

  const pdfSrc = useMemo(() => {
    if (renderType !== "pdf" || !presigned) return presigned;
    return page ? `${presigned}#page=${encodeURIComponent(page)}` : presigned;
  }, [presigned, renderType, page]);

  useEffect(() => {
    if (!open || !item || !auditRunId) return;
    let cancelled = false;

    const load = async () => {
      setStatus("loading");
      setPresigned(null);
      setTextContent("");
      setWorkbook(null);
      setDocxHtml("");
      setMeta({});
      try {
        const { data } = await API.get(
          `/audit-runs/${auditRunId}/documents/${item.id}/download`
        );
        if (cancelled) return;
        setPresigned(data.download_url);
        setMeta(data);
        setStatus("ready");

        if (renderType === "spreadsheet" && ext !== "csv") {
          const res = await fetch(data.download_url);
          if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
          const buffer = await res.arrayBuffer();
          if (cancelled) return;
          setWorkbook(XLSX.read(buffer, { type: "array" }));
        } else if (renderType === "text" || renderType === "json" || ext === "csv") {
          const res = await fetch(data.download_url);
          if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
          const text = await res.text();
          if (cancelled) return;
          setTextContent(text.slice(0, MAX_TEXT_CHARS));
        } else if (renderType === "docx") {
          const res = await fetch(data.download_url);
          if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
          const buffer = await res.arrayBuffer();
          if (cancelled) return;
          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          setDocxHtml(result.value);
        }
      } catch (error) {
        if (cancelled) return;
        setStatus("error");
        toast.error("Failed to load file preview");
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, item?.id, auditRunId, renderType, ext]);

  const tooLargeForParse =
    renderType === "spreadsheet" || renderType === "docx" || renderType === "text"
      ? (meta.file_size || 0) > MAX_PARSE_BYTES
      : false;

  const csvRows = useMemo(
    () => (ext === "csv" ? parseCsvRows(textContent) : null),
    [ext, textContent]
  );

  const jsonPretty = useMemo(() => {
    if (renderType !== "json") return textContent || "Empty file.";
    try {
      return JSON.stringify(JSON.parse(textContent || "{}"), null, 2);
    } catch {
      return textContent || "Empty file.";
    }
  }, [renderType, textContent]);

  const showUnsupported =
    renderType === "unsupported" ||
    (renderType !== "pdf" && renderType !== "image" && !presigned) ||
    tooLargeForParse;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader className="flex flex-row items-center justify-between !pb-4 !pr-14">
          <div className="min-w-0">
            <DialogTitle className="flex items-center gap-2 truncate">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate">{item?.filename || "File Preview"}</span>
            </DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-white/30 bg-white/10 text-white text-[10px] uppercase">
                {ext || "file"}
              </Badge>
              {meta.file_size ? (
                <span className="text-xs text-white/72">{formatSize(meta.file_size)}</span>
              ) : null}
            </DialogDescription>
          </div>
          {presigned && renderType !== "unsupported" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => window.open(pdfSrc, "_blank", "noopener,noreferrer")}
              className="shrink-0 bg-white/15 text-white hover:bg-white/25"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open
            </Button>
          )}
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto">
          {status === "loading" && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
            </div>
          )}

          {status === "error" && (
            <UnsupportedView filename={item?.filename} url={presigned} />
          )}

          {status === "ready" && presigned && (
            <>
              {renderType === "pdf" && (
                <iframe
                  src={pdfSrc}
                  title={item?.filename}
                  className="h-[68vh] w-full rounded-xl border border-border/60"
                />
              )}

              {renderType === "image" && (
                <div className="flex items-center justify-center p-4">
                  <img
                    src={presigned}
                    alt={item?.filename}
                    className="max-h-[65vh] max-w-full rounded-xl object-contain"
                  />
                </div>
              )}

              {showUnsupported && <UnsupportedView filename={item?.filename} url={presigned} />}

              {!showUnsupported && renderType === "spreadsheet" && ext === "csv" && (
                <CsvTable rows={csvRows} />
              )}

              {!showUnsupported && renderType === "spreadsheet" && ext !== "csv" && (
                <SpreadsheetTable workbook={workbook} />
              )}

              {!showUnsupported &&
                (renderType === "text" || renderType === "json") && (
                  <pre className="whitespace-pre-wrap break-words rounded-xl border border-border/60 bg-muted/20 p-4 text-xs font-mono leading-relaxed">
                    {jsonPretty}
                  </pre>
                )}

              {!showUnsupported && renderType === "docx" && (
                <div
                  className="docx-preview prose prose-sm max-w-none rounded-xl border border-border/60 p-6"
                  dangerouslySetInnerHTML={{ __html: docxHtml || "<p>Empty document.</p>" }}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
